import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, ListItem, ListItemText, Typography } from '@mui/material';
import TranscriptionCard from './TranscriptionCard';
import SpeakerConfirmationDialog from './SpeakerConfirmationDialog';
import StreamingAudioPlayer from './StreamingAudioPlayer';
import { updateTranscription, updateSummary, updateKeypoints } from '../Network';
import { transcriptionKey, summaryKey, keypointsKey, idKey } from '../MeetingsKeysList';
import { getSentence } from '../translations';
import { 
  parseTranscription, 
  extractSpeakers, 
  replaceSpeakerInText 
} from './utils';
import { 
  DEFAULT_CARD_STATE, 
  INITIAL_LOAD_LIMIT, 
  RENDER_DELAY 
} from './constants';
import { BACKGROUND_COLORS } from './constants';
import { useSpeakerColors } from '../../context/ColorPickerContext';
import { useMeetingContext } from '../../context/MeetingContext';
import { ServerURL } from '../ServerURL';

// Helper function to fetch speaker color from backend
const fetchSpeakerColor = async (meetingId, speaker) => {
  try {
    const response = await fetch(
      `${ServerURL}/get_speaker_color/${meetingId}/${encodeURIComponent(speaker)}`,
      {
        method: 'GET',
        credentials: 'include'
      }
    );
    
    if (response.ok) {
      const data = await response.text();
      // The endpoint might return just the color string or JSON
      try {
        const jsonData = JSON.parse(data);
        return jsonData.color || jsonData;
      } catch {
        // If it's not JSON, treat it as a plain color string
        return data.trim();
      }
    }
    return null;
  } catch (error) {
    console.warn(`Failed to fetch color for speaker "${speaker}":`, error);
    return null;
  }
};

// Helper function to fetch all speaker colors for a meeting
const fetchAllSpeakerColors = async (meetingId, speakers) => {
  const colorPromises = speakers.map(async (speaker) => {
    const color = await fetchSpeakerColor(meetingId, speaker);
    return { speaker, color };
  });
  
  const results = await Promise.all(colorPromises);
  const colorMap = {};
  
  results.forEach(({ speaker, color }) => {
    if (color) {
      colorMap[speaker] = color;
    }
  });
  
  return colorMap;
};

// Helper function to initialize transcription data
const initializeTranscriptionData = (meeting) => {
  if (meeting && meeting[transcriptionKey]) {
    const parsedTranscription = parseTranscription(meeting[transcriptionKey]);
    if (parsedTranscription && Array.isArray(parsedTranscription)) {
      const uniqueSpeakers = extractSpeakers(parsedTranscription);
      const initialCardStates = {};
      parsedTranscription.forEach((_, index) => {
        initialCardStates[index] = { ...DEFAULT_CARD_STATE };
      });
      return { parsedTranscription, uniqueSpeakers, initialCardStates };
    }
  }
  return { parsedTranscription: null, uniqueSpeakers: [], initialCardStates: {} };
};

const TranscriptionViewer = ({ meeting, meeting_id, onModalStateChange }) => {
  const { 
    setMeetingColors,
    loadCachedColors,
    isMeetingColorsLoaded,
    getSpeakerColor
  } = useSpeakerColors();
  
  const { refreshSelectedMeeting } = useMeetingContext();
  
  // Initialize data once
  const initialData = useMemo(() => initializeTranscriptionData(meeting), [meeting]);

  // State management
  const [transcription, setTranscription] = useState(initialData.parsedTranscription);
  const [speakers, setSpeakers] = useState(initialData.uniqueSpeakers);
  const [cardStates, setCardStates] = useState(initialData.initialCardStates);

  // Extract transcription data for stable dependencies
  const transcriptionData = meeting[transcriptionKey];
  const meetingId = meeting[idKey];

  // Note: transcriptionTextHash was removed as it's no longer needed for state sync

  // Sync local state with meeting data when it changes (for incremental updates)
  // Only sync when we're switching meetings or when transcription length increases (new segments added)
  useEffect(() => {
    const newData = initializeTranscriptionData(meeting);
    const currentTranscriptionLength = transcription?.length || 0;
    const newTranscriptionLength = newData.parsedTranscription?.length || 0;
    
    // Check if this is a different meeting than what we currently have loaded
    const isDifferentMeeting = currentMeetingIdRef.current !== meeting_id;
    
    // Only sync if:
    // 1. Different meeting (meeting switch)
    // 2. New segments added (length increased) - for live transcription updates
    // 3. We have no local transcription but meeting has data (initial load)
    const shouldSync = isDifferentMeeting || 
                      (newTranscriptionLength > currentTranscriptionLength) ||
                      (!transcription && newData.parsedTranscription);
    
    if (shouldSync) {
      setTranscription(newData.parsedTranscription);
      setSpeakers(newData.uniqueSpeakers);
      setCardStates(prevStates => ({
        ...prevStates,
        ...newData.initialCardStates
      }));
    }
  }, [transcriptionData, meetingId, meeting, meeting_id, transcription]); // Include transcription for linting
  const [dialogState, setDialogState] = useState({
    open: false,
    oldSpeaker: '',
    newSpeaker: '',
    pendingChange: null
  });
  // Remove UI-as-state; we'll render directly from data
  const [currentPlayingSegment, setCurrentPlayingSegment] = useState(null);
  
  // Refs
  const streamingAudioPlayerRef = useRef(null);
  const currentMeetingIdRef = useRef(meeting_id);

  // Calculate meeting duration from transcription - memoized for stability
  const getMeetingDuration = useMemo(() => {
    if (!transcription || !transcription.length) return null;
    
    const lastSegment = transcription[transcription.length - 1];
    return lastSegment?.end || null;
  }, [transcription]);

  // Properly memoize speakers to ensure re-renders when speakers change
  const memoizedSpeakers = useMemo(() => speakers, [speakers]);

  // Create transcription segments data for StreamingAudioPlayer
  const transcriptionSegments = useMemo(() => {
    if (!transcription || !memoizedSpeakers.length) return [];
    return transcription
      .map(segment => {
        const speakerIndex = memoizedSpeakers.indexOf(segment.speaker);
        // Use actual speaker color from context, fallback to BACKGROUND_COLORS
        const fallbackColor = BACKGROUND_COLORS[speakerIndex] || BACKGROUND_COLORS[0];
        const actualColor = getSpeakerColor(segment.speaker, [fallbackColor]);
        return {
          start: parseFloat(segment.start),
          end: parseFloat(segment.end),
          speaker: segment.speaker,
          color: actualColor,
          text: segment.text
        };
      })
      .sort((a, b) => a.start - b.start);
  }, [transcription, memoizedSpeakers, getSpeakerColor]);

  // Update transcription data only when meeting changes (different meeting selected)
  useEffect(() => {
    // Check if this is a different meeting than what we currently have loaded
    const isDifferentMeeting = currentMeetingIdRef.current !== meeting_id;
    
    if (isDifferentMeeting || (!transcription && initialData.parsedTranscription)) {
      setTranscription(initialData.parsedTranscription);
      setSpeakers(initialData.uniqueSpeakers);
      setCardStates(initialData.initialCardStates);
      setVisibleCount(INITIAL_LOAD_LIMIT);
      currentMeetingIdRef.current = meeting_id;
    }
  }, [meeting_id, initialData.parsedTranscription, initialData.uniqueSpeakers, initialData.initialCardStates, transcription]);

  // Load speaker colors from backend when speakers change - with smart caching
  useEffect(() => {
    if (!meeting_id || !speakers || speakers.length === 0) return;

    // First, try to load from cache
    if (isMeetingColorsLoaded(meeting_id)) {
      // Colors are already cached, just load them into active state
      loadCachedColors(meeting_id);
      return;
    }

    // If not cached, fetch from backend (with debouncing to prevent excessive API calls)
    const timeoutId = setTimeout(async () => {
      try {
        const colors = await fetchAllSpeakerColors(meeting_id, speakers);
        // Use the new caching method instead of the old one
        setMeetingColors(meeting_id, colors);
      } catch (error) {
        console.warn('Failed to load speaker colors:', error);
        // Continue with fallback colors
      }
    }, 100); // Small delay to batch rapid speaker changes

    return () => clearTimeout(timeoutId);
  }, [meeting_id, speakers, isMeetingColorsLoaded, loadCachedColors, setMeetingColors]);

  // Progressive visibility instead of UI-as-state
  const [visibleCount, setVisibleCount] = useState(INITIAL_LOAD_LIMIT);
  useEffect(() => {
    if (!transcription) return;
    if (visibleCount < transcription.length) {
      const id = setTimeout(() => {
        setVisibleCount(transcription.length);
      }, RENDER_DELAY);
      return () => clearTimeout(id);
    }
  }, [transcription, visibleCount]);

  // Text editing handlers
  const handleTextEdit = useCallback(async (index, newText) => {
    if (!transcription || transcription[index]?.text === newText) return;

    const updatedTranscription = [...transcription];
    updatedTranscription[index] = { ...updatedTranscription[index], text: newText };

    setTranscription(updatedTranscription);
    await updateTranscription(meeting_id, updatedTranscription);
    
    // Refresh the meeting data in context to prevent state sync issues
    if (refreshSelectedMeeting) {
      await refreshSelectedMeeting();
    }
  }, [transcription, meeting_id, refreshSelectedMeeting]);

  const handleTextDoubleClick = useCallback((index) => {
    setCardStates(prev => ({
      ...prev,
      [index]: { ...prev[index], editableText: true }
    }));
  }, []);

  const handleEditClick = useCallback((index) => {
    setCardStates(prev => ({
      ...prev,
      [index]: { ...prev[index], editableText: true }
    }));
  }, []);

  const handleSaveClick = useCallback((index) => {
    setCardStates(prev => ({
      ...prev,
      [index]: { ...prev[index], editableText: false }
    }));
  }, []);

  // Perform speaker update - defined before handleSpeakerEdit to avoid dependency issues
  const performSpeakerUpdate = useCallback(async (index, newSpeaker, replaceAll = false) => {
    if (!transcription) return;

    const oldSpeaker = transcription[index]?.speaker;
    let updatedTranscription = [...transcription];
    let updatedSpeakers = [...speakers];

    if (replaceAll) {
      // Replace all occurrences
      updatedTranscription.forEach((segment, i) => {
        if (segment?.speaker === oldSpeaker) {
          updatedTranscription[i] = {
            ...updatedTranscription[i],
            speaker: newSpeaker
          };
        }
      });

      // Update speakers list
      const oldIndex = updatedSpeakers.indexOf(oldSpeaker);
      if (oldIndex > -1 && !updatedSpeakers.includes(newSpeaker)) {
        updatedSpeakers[oldIndex] = newSpeaker;
      }

      // Update summary and keypoints
      const updatedSummary = replaceSpeakerInText(meeting[summaryKey], oldSpeaker, newSpeaker);
      const updatedKeypoints = replaceSpeakerInText(meeting[keypointsKey], oldSpeaker, newSpeaker);

      await Promise.all([
        updateTranscription(meeting_id, updatedTranscription),
        updateSummary(meeting_id, updatedSummary),
        updateKeypoints(meeting_id, updatedKeypoints)
      ]);

      // Refresh the meeting data in context to prevent state sync issues
      if (refreshSelectedMeeting) {
        await refreshSelectedMeeting();
      }
    } else {
      // Replace single occurrence
      updatedTranscription[index] = {
        ...updatedTranscription[index],
        speaker: newSpeaker
      };

      if (!updatedSpeakers.includes(newSpeaker)) {
        updatedSpeakers.push(newSpeaker);
      }

      await updateTranscription(meeting_id, updatedTranscription);

      // Refresh the meeting data in context to prevent state sync issues
      if (refreshSelectedMeeting) {
        await refreshSelectedMeeting();
      }
    }

    setTranscription(updatedTranscription);
    setSpeakers(updatedSpeakers);
    
    // Reset card state
    setCardStates(prev => ({
      ...prev,
      [index]: { ...prev[index], editableSpeaker: false, hooverSpeaker: false }
    }));
  }, [transcription, speakers, meeting, meeting_id, refreshSelectedMeeting]);

  // Speaker editing handlers
  const handleSpeakerEdit = useCallback(async (index, newSpeaker) => {
    if (typeof newSpeaker !== 'string') {
      try {
        newSpeaker = newSpeaker.originalValue;
      } catch {
        return;
      }
    } else {
      // Handle the case where Enter key passes the display text with "Add " prefix
      const addPrefix = getSentence(76); // "Add " 
      if (newSpeaker.startsWith(addPrefix)) {
        newSpeaker = newSpeaker.substring(addPrefix.length);
      }
    }

    // Ensure we have a valid string speaker name
    if (!newSpeaker || typeof newSpeaker !== 'string') {
      return;
    }

    const oldSpeaker = transcription[index]?.speaker;
    if (!oldSpeaker || oldSpeaker === newSpeaker) return;

    // Check if speaker appears multiple times
    const occurrences = transcription.filter(segment => 
      segment?.speaker === oldSpeaker
    ).length;

    if (occurrences > 1) {
      setDialogState({
        open: true,
        oldSpeaker,
        newSpeaker,
        pendingChange: { index, newSpeaker }
      });
    } else {
      await performSpeakerUpdate(index, newSpeaker, false);
    }

    // If this is a new speaker, fetch their color from the backend and update cache
    if (!speakers.includes(newSpeaker)) {
      try {
        const speakerColor = await fetchSpeakerColor(meeting_id, newSpeaker);
        if (speakerColor) {
          // Get current cached colors for this meeting or create new color map
          const currentColors = isMeetingColorsLoaded(meeting_id) 
            ? { ...speakers.reduce((acc, speaker) => ({ ...acc, [speaker]: null }), {}) }
            : {};
          
          setMeetingColors(meeting_id, {
            ...currentColors,
            [newSpeaker]: speakerColor
          });
        }
      } catch (error) {
        console.warn(`Failed to fetch color for new speaker "${newSpeaker}":`, error);
      }
    }
  }, [transcription, performSpeakerUpdate, speakers, meeting_id, isMeetingColorsLoaded, setMeetingColors]);

  const handleSpeakerDoubleClick = useCallback((index) => {
    setCardStates(prev => ({
      ...prev,
      [index]: { ...prev[index], editableSpeaker: true }
    }));
  }, []);

  // Dialog handlers
  const handleDialogConfirm = useCallback(async () => {
    const { pendingChange, newSpeaker } = dialogState;
    if (pendingChange) {
      await performSpeakerUpdate(pendingChange.index, newSpeaker, true);
    }
    setDialogState({ open: false, oldSpeaker: '', newSpeaker: '', pendingChange: null });
  }, [dialogState, performSpeakerUpdate]);

  const handleDialogNo = useCallback(async () => {
    const { pendingChange, newSpeaker } = dialogState;
    if (pendingChange) {
      // Perform single speaker change (replaceAll = false)
      await performSpeakerUpdate(pendingChange.index, newSpeaker, false);
    }
    setDialogState({ open: false, oldSpeaker: '', newSpeaker: '', pendingChange: null });
  }, [dialogState, performSpeakerUpdate]);

  // Mouse interaction handlers
  const handleCardMouseEnter = useCallback((index) => {
    setCardStates(prev => {
      const currentState = prev[index];
      if (currentState?.hooverCard || currentState?.hooverSpeaker) return prev;

      return {
        ...prev,
        [index]: { ...currentState, hooverCard: true }
      };
    });
  }, []);

  const handleCardMouseLeave = useCallback((index) => {
    setCardStates(prev => {
      const currentState = prev[index];
      if (currentState?.editableSpeaker || currentState?.editableText) return prev;

      return {
        ...prev,
        [index]: { ...currentState, hooverCard: false, hooverSpeaker: false }
      };
    });
  }, []);


  // Optimized segment lookup using sorted segments
  const sortedSegments = useMemo(() => {
    if (!transcription) return [];
    
    return transcription
      .map((segment, index) => ({
        index,
        start: parseFloat(segment?.start) || 0,
        end: parseFloat(segment?.end) || 0
      }))
      .filter(seg => seg.start !== undefined && seg.end !== undefined)
      .sort((a, b) => a.start - b.start);
  }, [transcription]);

  // Handle time updates from StreamingAudioPlayer
  const handleTimeUpdate = useCallback((currentTime, isPlaying) => {
    if (!transcription || !isPlaying || sortedSegments.length === 0) {
      if (currentPlayingSegment !== null) {
        setCurrentPlayingSegment(null);
      }
      return;
    }

    // Use binary search for better performance with large transcriptions
    let left = 0;
    let right = sortedSegments.length - 1;
    let found = null;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const segment = sortedSegments[mid];
      
      if (currentTime >= segment.start && currentTime <= segment.end) {
        found = segment.index;
        break;
      } else if (currentTime < segment.start) {
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }

    // Only update state if the segment actually changed
    if (found !== currentPlayingSegment) {
      setCurrentPlayingSegment(found);
    }
  }, [transcription, sortedSegments, currentPlayingSegment]);

  // Audio playback handler - now uses StreamingAudioPlayer seek functionality
  const handleAudioSeek = useCallback((index) => {
    if (!transcription || !streamingAudioPlayerRef.current) return;

    const segment = transcription[index];
    if (segment && segment.start !== undefined) {
      streamingAudioPlayerRef.current.seekToTime(parseFloat(segment.start), true);
    }
  }, [transcription]);

  // Track the target card index for navigation to handle rapid clicks
  const targetCardIndexRef = useRef(null);
  const navigationTimeoutRef = useRef(null);
  
  // Refs for transcription cards to enable scrolling
  const cardRefs = useRef(new Map());
  
  // Function to scroll to a specific card
  const scrollToCard = useCallback((cardIndex) => {
    const cardElement = cardRefs.current.get(cardIndex);
    if (cardElement) {
      cardElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  }, []);
  
  // Handle card focus change from audio player (timeline clicks, skip buttons, etc.)
  const handleCardFocusChange = useCallback((currentTime) => {
    if (!transcription || transcription.length === 0) return;
    
    // Find the segment that contains the current time
    const targetIndex = transcription.findIndex(segment => {
      return segment && currentTime >= parseFloat(segment.start) && currentTime <= parseFloat(segment.end);
    });
    
    // If we found a matching segment, scroll to it
    if (targetIndex !== -1) {
      scrollToCard(targetIndex);
    } else {
      // If no exact match, find the closest segment after the current time
      const closestIndex = transcription.findIndex(segment => {
        return segment && parseFloat(segment.start) >= currentTime;
      });
      
      if (closestIndex !== -1) {
        scrollToCard(closestIndex);
      }
    }
  }, [transcription, scrollToCard]);

  // Card navigation handlers with improved rapid-click handling
  const handlePreviousCard = useCallback(() => {
    if (!transcription || transcription.length === 0) return;

    // Clear any pending navigation timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    // Determine current index - use target index if we're in the middle of navigation
    let currentIndex;
    if (targetCardIndexRef.current !== null) {
      currentIndex = targetCardIndexRef.current;
    } else if (currentPlayingSegment !== null) {
      currentIndex = currentPlayingSegment;
    } else {
      currentIndex = 0; // Start from first if nothing is playing
    }

    // Calculate previous index
    const previousIndex = Math.max(0, currentIndex - 1);
    targetCardIndexRef.current = previousIndex;

    // Navigate to the previous card
    handleAudioSeek(previousIndex);

    // Reset target index after a delay to allow audio player to catch up
    navigationTimeoutRef.current = setTimeout(() => {
      targetCardIndexRef.current = null;
    }, 1000);

  }, [transcription, currentPlayingSegment, handleAudioSeek]);

  const handleNextCard = useCallback(() => {
    if (!transcription || transcription.length === 0) return;

    // Clear any pending navigation timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    // Determine current index - use target index if we're in the middle of navigation
    let currentIndex;
    if (targetCardIndexRef.current !== null) {
      currentIndex = targetCardIndexRef.current;
    } else if (currentPlayingSegment !== null) {
      currentIndex = currentPlayingSegment;
    } else {
      currentIndex = -1; // Will become 0 after increment
    }

    // Calculate next index
    const nextIndex = Math.min(transcription.length - 1, currentIndex + 1);
    targetCardIndexRef.current = nextIndex;

    // Navigate to the next card
    handleAudioSeek(nextIndex);

    // Reset target index after a delay to allow audio player to catch up
    navigationTimeoutRef.current = setTimeout(() => {
      targetCardIndexRef.current = null;
    }, 1000);

  }, [transcription, currentPlayingSegment, handleAudioSeek]);

  // Cleanup navigation timeout on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  // No UI rebuild side effects needed

  // StreamingAudioPlayer component
  const streamingPlayer = (
    <StreamingAudioPlayer
      ref={streamingAudioPlayerRef}
      meetingId={meeting_id}
      meetingDuration={getMeetingDuration}
      segments={transcriptionSegments}
      speakers={memoizedSpeakers}
      onTimeUpdate={handleTimeUpdate}
      onPreviousCard={handlePreviousCard}
      onNextCard={handleNextCard}
      onCardFocusChange={handleCardFocusChange}
    />
  );

  // Only show loading if meeting doesn't have transcription data
  if (!transcription && meeting && meeting[transcriptionKey]) {
    return <Box>Loading transcription...</Box>;
  }
  
  // If meeting has no transcription data, show appropriate message
  if (!transcription) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        height="200px"
      >
        <Typography variant="body2" color="textSecondary">
          Transcription not available for this meeting.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Streaming Audio Player */}
      {streamingPlayer}
      
      {/* Transcription Cards */}
      {transcription && transcription.slice(0, visibleCount).map((segment, index) => {
        const cardState = cardStates[index] || DEFAULT_CARD_STATE;
        const isCurrentlyPlaying = currentPlayingSegment === index;
        return (
          <ListItem 
            key={`${index}-${segment.speaker}-${segment.start}`} 
            alignItems="flex-start"
            ref={(el) => {
              if (el) {
                cardRefs.current.set(index, el);
              } else {
                cardRefs.current.delete(index);
              }
            }}
          >
            <ListItemText 
              primary={
                <TranscriptionCard
                  itemKey={index}
                  index={index}
                  segment={segment}
                  speakers={memoizedSpeakers}
                  cardState={cardState}
                  isCurrentlyPlaying={isCurrentlyPlaying}
                  meetingId={meeting_id}
                  onTextEdit={handleTextEdit}
                  onSpeakerEdit={handleSpeakerEdit}
                  onMouseEnter={handleCardMouseEnter}
                  onMouseLeave={handleCardMouseLeave}
                  onTextDoubleClick={handleTextDoubleClick}
                  onSpeakerDoubleClick={handleSpeakerDoubleClick}
                  onEditClick={handleEditClick}
                  onAudioSeek={handleAudioSeek}
                  onSaveClick={handleSaveClick}
                />
              }
            />
          </ListItem>
        );
      })}
      
      {/* Speaker Confirmation Dialog */}
      <SpeakerConfirmationDialog
        open={dialogState.open}
        oldSpeaker={dialogState.oldSpeaker}
        newSpeaker={dialogState.newSpeaker}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogNo}
      />
    </Box>
  );
};

TranscriptionViewer.displayName = 'TranscriptionViewer';

export default TranscriptionViewer; 